import { useState, useEffect, useRef } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { ArrowLeft, MoreHorizontal, Paperclip, Send as SendIcon, FileText, ImageIcon, Clock, CheckCircle, AlertCircle, XCircle, PlayCircle, Italic, Download, X } from "lucide-react";
import EmojiPicker from "emoji-picker-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import CaSidebar from "../startupdashboard/admin_sidebar";
import { getCaFormMessages, sendCaFormMessage, getFormFields, uploadFile, deleteFile, updateFormResponseStatus, uploadImage } from "../utils/api";
import { format, isToday, isYesterday } from 'date-fns';
import io from "socket.io-client";
import ModernHeader from "../startupdashboard/modern-header";
import { Document, Page, pdfjs } from 'react-pdf';

const TICKET_STATUS = {
  open: { label: "Open", color: "bg-blue-100 text-blue-800", icon: AlertCircle },
  in_progress: { label: "In Progress", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  pending: { label: "Pending", color: "bg-orange-100 text-orange-800", icon: PlayCircle },
  completed: { label: "Completed", color: "bg-green-100 text-green-800", icon: CheckCircle },
  closed: { label: "Closed", color: "bg-gray-100 text-gray-800", icon: XCircle },
};

function Avatar({ name }) {
  const initial = name?.charAt(0).toUpperCase() || "?";
  return (
    <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-white font-semibold">
      {initial}
    </div>
  );
}

function StatusBadge({ status, onChange }) {
  const cfg = TICKET_STATUS[status] || TICKET_STATUS.open;
  const Icon = cfg.icon;
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className={`inline-flex items-center space-x-1 px-3 py-1 rounded-lg text-sm ${cfg.color}`}>
        <Icon className="w-4 h-4" />
        <span>{cfg.label}</span>
        <MoreHorizontal className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-40 bg-white border rounded-lg shadow-lg z-50">
          {Object.entries(TICKET_STATUS).map(([key, c]) => (
            <button
              key={key}
              onClick={() => { onChange(key); setOpen(false); }}
              className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center space-x-2">
              <c.icon className="w-4 h-4" />
              <span>{c.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MessageStatus({ status, isCurrentUser }) {
  if (!isCurrentUser) return null;

  return (
    <span className="ml-2">
      {status === 'read' ? (
        <span className="text-blue-500">✓✓</span>
      ) : status === 'delivered' ? (
        <span className="text-gray-400">✓</span>
      ) : (
        <span className="text-gray-300">✓</span>
      )}
    </span>
  );
}

const formatTime = ts => {
  if (!ts) return '';
  const date = new Date(ts);
  if (isNaN(date.getTime())) return '';
  return format(date, "hh:mm a");
};

function getFileIcon(type) {
  switch (type) {
    case 'pdf':
      return <FileText className="w-4 h-4 text-red-500" />;
    case 'image':
      return <ImageIcon className="w-4 h-4 text-blue-500" />;
    default:
      return <FileText className="w-4 h-4 text-gray-500" />;
  }
}

function throttle(func, limit) {
  let lastFunc;
  let lastRan;
  return function () {
    const context = this;
    const args = arguments;
    if (!lastRan) {
      func.apply(context, args);
      lastRan = Date.now();
    } else {
      clearTimeout(lastFunc);
      lastFunc = setTimeout(function () {
        if ((Date.now() - lastRan) >= limit) {
          func.apply(context, args);
          lastRan = Date.now();
        }
      }, limit - (Date.now() - lastRan));
    }
  };
}

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

function FilePreviewModal({ file, onClose }) {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const isPdf = file?.url?.endsWith('.pdf');
  const isImage = file?.type === 'image';

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = file.url;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center border-b p-4">
          <h3 className="text-lg font-medium">{file.name}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="flex-1 overflow-auto p-4 flex flex-col items-center">
          {isPdf ? (
            <>
              <div className="border rounded-lg overflow-hidden mb-4">
                <Document
                  file={file.url}
                  onLoadSuccess={onDocumentLoadSuccess}
                  loading={<div className="p-8">Loading PDF...</div>}
                  error={<div className="p-8 text-red-500">Failed to load PDF</div>}
                >
                  <Page
                    pageNumber={pageNumber}
                    width={Math.min(800, window.innerWidth - 100)}
                    renderTextLayer={false}
                  />
                </Document>
              </div>
              <div className="flex items-center justify-center space-x-4">
                <button
                  disabled={pageNumber <= 1}
                  onClick={() => setPageNumber(p => Math.max(1, p - 1))}
                  className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
                >
                  Previous
                </button>
                <span>
                  Page {pageNumber} of {numPages || '--'}
                </span>
                <button
                  disabled={pageNumber >= (numPages || 0)}
                  onClick={() => setPageNumber(p => Math.min(numPages || p, p + 1))}
                  className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </>
          ) : isImage ? (
            <img
              src={file.url}
              alt={file.name}
              className="max-h-[70vh] max-w-full object-contain"
            />
          ) : (
            <div className="flex flex-col items-center justify-center p-8">
              <FileText className="w-16 h-16 text-gray-400 mb-4" />
              <p className="text-gray-500 mb-6">Preview not available for this file type</p>
            </div>
          )}
        </div>
        <div className="border-t p-4 flex justify-between items-center">
          <div className="text-sm text-gray-500">
            {file.size ? `Size: ${formatFileSize(file.size)}` : ''}
          </div>
          <button
            onClick={handleDownload}
            className="flex items-center space-x-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg"
          >
            <Download className="w-4 h-4" />
            <span>Download</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// Helper function to format file size
function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export default function CaChatModal({ ticket, currentUser, onClose }) {
  const { ticketId } = useParams();
  const location = useLocation();

  const caProfile = JSON.parse(localStorage.getItem('user') || '{}');
  const caId = caProfile._id;
  const startupId = localStorage.getItem('startupId');
  const currentUserName = localStorage.getItem('startupName');
  const logoUrl = localStorage.getItem('logoUrl');

  const scrollRef = useRef(null);
  const socketRef = useRef(null);
  const textareaRef = useRef();
  const fileInputRef = useRef();
  const imageInputRef = useRef();
  const typingTimeoutRef = useRef(null);

  const [fields, setFields] = useState([]);
  const [responseData, setResponseData] = useState({});
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const initialStatus =
    ticket?.status ||
    location.state?.ticket?.status ||
    'open';
  const [status, setStatus] = useState(initialStatus);
  const [typingUsers, setTypingUsers] = useState({});
  const [isTyping, setIsTyping] = useState(false);
  const [formData, setFormData] = useState(null);
  const [isChatVisible, setIsChatVisible] = useState(true);

  const mergedTicket = {
    id: ticket?.id || location.state?.ticket?.id || ticketId,
    title: ticket?.formName || location.state?.ticket?.formName || '',
    status: ticket?.status || location.state?.ticket?.status || 'open',
    assignedCA: { name: ticket?.assignedCA?.name || '' },
  };

  const normalizeMessage = (msg) => {
    const isCurrentUser = msg.senderId?.toString() === startupId?.toString();
    const isCA = msg.senderRole === 'CA' ||
      (msg.senderId?.toString() === caId?.toString() && !isCurrentUser);

    return {
      ...msg,
      id: msg.id || msg._id || `temp-${Date.now()}`,
      senderId: msg.senderId || (isCA ? caId : startupId),
      senderName: msg.senderName || (isCA ? 'CA' : currentUserName),
      senderRole: msg.senderRole || (isCA ? 'CA' : 'USER'),
      timestamp: msg.timestamp || new Date().toISOString(),
      isCurrentUser
    };
  };

  useEffect(() => {
    if (!ticketId) return;

    console.log(`[User] Initializing for ticket ${ticketId}`);
    (async () => {
      try {
        const [fieldsRes, payload] = await Promise.all([
          getFormFields(ticketId),
          getCaFormMessages(ticketId),
        ]);

        console.log("[Init] raw payload:", payload);

        // Start with payload.data if present, or payload itself
        let answers = payload?.data ?? payload ?? {};
        // Unwrap nested shapes like payload.data.data or payload.data.responses
        if (answers && typeof answers === 'object') {
          if (answers.data && typeof answers.data === 'object') answers = answers.data;
          if (answers.responses && typeof answers.responses === 'object') answers = answers.responses;
          if (answers.answers && typeof answers.answers === 'object') answers = answers.answers;
        }

        // createdAt/time normalization
        const createdAt = payload?.time ?? payload?.createdAt ?? answers?.createdAt ?? payload?.data?.time;
        if (createdAt) answers = { ...answers, _createdAt: createdAt };

        // Extract contact values from many possible places
        const possibleEmail =
          answers?.userEmail ??
          answers?.email ??
          payload?.userEmail ??
          payload?.email ??
          payload?.data?.userEmail ??
          payload?.data?.email ??
          payload?.startup?.email ??
          null;

        const possiblePhone =
          answers?.userPhone ??
          answers?.phone ??
          answers?.mobile ??
          payload?.userPhone ??
          payload?.phone ??
          payload?.mobile ??
          payload?.data?.userPhone ??
          payload?.data?.phone ??
          null;

        const contactEmail = typeof possibleEmail === 'string' ? possibleEmail.trim() : null;
        const contactPhone = typeof possiblePhone === 'string' ? possiblePhone.trim() : null;

        answers = { ...(answers || {}), _contactEmail: contactEmail, _contactPhone: contactPhone };

        setFields(fieldsRes || []);
        setResponseData(answers || {});
        setFormData(payload?.data ?? {});
        setStatus(payload?.status || status);

        const contactEmailFallback = payload?.data?._contactEmail ?? payload?.data?.userEmail ?? payload?.userEmail ?? payload?.startup?.email ?? null;
        const contactPhoneFallback = payload?.data?._contactPhone ?? payload?.data?.userPhone ?? payload?.userPhone ?? null;

        const uniqueMessages = (payload.messages || []).reduce((acc, msg) => {
          const normalized = normalizeMessage({
            ...msg,
            // attach fallbacks so each message may carry contact info if available
            userEmail: msg.userEmail ?? msg.email ?? contactEmailFallback,
            userPhone: msg.userPhone ?? msg.phone ?? msg.mobile ?? contactPhoneFallback,
          });

          if (!acc.some(m => m.id === normalized.id)) acc.push(normalized);
          return acc;
        }, []);
        setMessages(uniqueMessages);

        console.log("[Init] normalized answers:", answers);
        console.log("CONTACT VALUES:", { contactEmail, contactPhone });
      } catch (err) {
        console.error("[User] Fetch error:", err);
      }
    })();
  }, [ticketId]);

  const getAnswer = (f) => {
    if (!f) return null;
    return (
      responseData?.[f._id] ??
      responseData?.[f.name] ??
      responseData?.responses?.[f._id] ??
      responseData?.answers?.[f._id] ??
      responseData?.[f._id + ''] ?? // defensive
      null
    );
  };

  useEffect(() => {
    const socket = io(process.env.REACT_APP_API_URL || "http://localhost:8000", {
      autoConnect: false,
      transports: ["websocket"]
    });
    socketRef.current = socket;

    console.log("[Socket] about to connect…");
    socket.connect();

    socket.on("connect", () => {
      console.log("[Socket] connected with id:", socket.id);
      socket.emit("joinForm", ticketId);
    });

    socket.on("connect_error", (err) => {
      console.error("[Socket] connection error:", err);
    });

    socket.on("disconnect", (reason) => {
      console.warn("[Socket] disconnected:", reason);
    });

    socket.on("joinedForm", (roomId) => {
      console.log(`[Socket] server ack'd joinForm for room ${roomId}`);
    });

    const handleNewMessage = (rawMsg) => {
      if (rawMsg.senderId === startupId && rawMsg.tempId) {
        return;
      }

      setMessages(prev => {
        let withoutTemp = prev;
        if (rawMsg.tempId) {
          withoutTemp = prev.filter(m => m.tempId !== rawMsg.tempId);
        }

        if (withoutTemp.some(m => m.id === rawMsg.id)) {
          return withoutTemp;
        }

        return [...withoutTemp, normalizeMessage(rawMsg)];
      });
    };
    socket.on("newMessage", handleNewMessage);

    const handleUserTyping = data => {
      console.log("[Socket] userTyping:", data);
      setTypingUsers(u => ({ ...u, [data.userId]: data.userName }));
    };
    const handleUserStoppedTyping = data => {
      console.log("[Socket] userStoppedTyping:", data);
      setTypingUsers(u => {
        const copy = { ...u };
        delete copy[data.userId];
        return copy;
      });
    };

    socket.on("userTyping", handleUserTyping);
    socket.on("userStoppedTyping", handleUserStoppedTyping);
    socket.on("error", err => console.error("[Socket] error event:", err));

    return () => {
      console.log("[Socket] cleaning up listeners and disconnecting");
      socket.off("connect");
      socket.off("connect_error");
      socket.off("disconnect");
      socket.off("joinedForm");
      socket.off("newMessage", handleNewMessage);
      socket.off("userTyping", handleUserTyping);
      socket.off("userStoppedTyping", handleUserStoppedTyping);
      socket.disconnect();
    };
  }, [ticketId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, typingUsers]);

  useEffect(() => {
    if (!socketRef.current || !mergedTicket.id) return;

    const handleMessageDelivered = ({ messageId, status }) => {
      setMessages(prev => prev.map(msg =>
        msg.id === messageId ? { ...msg, status } : msg
      ));
    };

    const handleMessagesRead = ({ messageIds }) => {
      setMessages(prev => prev.map(msg =>
        messageIds.includes(msg.id) ? { ...msg, status: 'read' } : msg
      ));
    };

    socketRef.current.on('messageStatusUpdate', handleMessageDelivered);
    socketRef.current.on('messagesRead', handleMessagesRead);

    return () => {
      socketRef.current.off('messageStatusUpdate', handleMessageDelivered);
      socketRef.current.off('messagesRead', handleMessagesRead);
    };
  }, [mergedTicket.id]);

  useEffect(() => {
    if (!socketRef.current?.connected || !messages.length) return;

    const undelivered = messages.filter(
      msg => !msg.isCurrentUser && msg.status !== 'delivered' && msg.status !== 'read'
    );

    if (undelivered.length) {
      socketRef.current.emit('markAsDelivered', {
        messageId: undelivered[0].id,
        formId: mergedTicket.id
      });
    }
  }, [messages, mergedTicket.id]);

  useEffect(() => {
    if (!socketRef.current || !mergedTicket.id) return;

    const handleMessagesRead = ({ messageIds }) => {
      console.log('[USER] Received messagesRead event:', {
        messageIds,
        currentMessages: messages.map(m => ({ id: m.id, status: m.status }))
      });

      setMessages(prev => {
        const updated = prev.map(msg =>
          messageIds.includes(msg.id) ? { ...msg, status: 'read' } : msg
        );
        console.log('[USER] Updated messages after messagesRead:',
          updated.map(m => ({ id: m.id, status: m.status }))
        );
        return updated;
      });
    };

    socketRef.current.on('messagesRead', handleMessagesRead);

    return () => {
      console.log('[USER] Cleaning up messagesRead listener');
      socketRef.current.off('messagesRead', handleMessagesRead);
    };
  }, [mergedTicket.id, messages]);

  useEffect(() => {
    if (!socketRef.current?.connected || !messages.length || !isChatVisible) {
      console.log('[USER] Not marking as read - conditions not met:', {
        connected: socketRef.current?.connected,
        messagesLength: messages.length,
        isChatVisible
      });
      return;
    }

    const unread = messages
      .filter(msg => !msg.isCurrentUser && msg.status !== 'read')
      .map(msg => msg.id);

    if (unread.length) {
      console.log('[USER] Marking messages as read:', unread);

      setMessages(prev => {
        const updated = prev.map(msg =>
          unread.includes(msg.id) ? { ...msg, status: 'read' } : msg
        );
        console.log('[USER] Optimistic update before socket emit:',
          updated.map(m => ({ id: m.id, status: m.status }))
        );
        return updated;
      });

      socketRef.current.emit('markAsRead', {
        messageIds: unread,
        formId: mergedTicket.id
      });
    }
  }, [messages, mergedTicket.id, isChatVisible]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = document.visibilityState === 'visible' &&
        document.hasFocus();
      console.log('Visibility changed:', {
        visibilityState: document.visibilityState,
        hasFocus: document.hasFocus(),
        isChatVisible: isVisible
      });
      setIsChatVisible(isVisible);
    };

    const handleFocus = () => {
      console.log('Window gained focus');
      setIsChatVisible(true);
    };

    const handleBlur = () => {
      console.log('Window lost focus');
      setIsChatVisible(false);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    handleVisibilityChange();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  const handleInputChange = (e) => {
    setInput(e.target.value);

    if (!isTyping) {
      socketRef.current?.emit('typing', {
        formId: mergedTicket.id,
        userId: caId,
        userName: currentUserName
      });
      setIsTyping(true);
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      socketRef.current?.emit('stoppedTyping', {
        formId: mergedTicket.id,
        userId: caId
      });
      setIsTyping(false);
    }, 1500);
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    const uploaded = [];

    for (let file of files) {
      try {
        let res;
        if (file.type.startsWith('image/')) {
          res = await uploadImage(file);
        } else {
          res = await uploadFile(file);
        }

        const isPdf = file.name.endsWith('.pdf');
        const isImage = file.type.startsWith('image/') || file.name.match(/\.(jpg|jpeg|png|gif|webp)$/i);

        uploaded.push({
          name: file.name,
          size: res.bytes,
          type: isImage ? 'image' : isPdf ? 'pdf' : 'doc',
          url: res.url,
          publicId: res.publicId,
        });
      } catch (err) {
        console.error('Upload failed:', err);
      }
    }
    setAttachments(prev => [...prev, ...uploaded]);
    setShowAttachMenu(false);
  };

  const handleEmojiClick = (emojiData) => {
    setInput(prev => prev + emojiData.emoji);
    setShowEmojiPicker(false);
  };

  const wrapSelection = (prefix, suffix = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = input.substring(start, end);
    const before = input.substring(0, start);
    const after = input.substring(end);
    setInput(before + prefix + selectedText + suffix + after);
    setTimeout(() => {
      textarea.selectionStart = start + prefix.length;
      textarea.selectionEnd = start + prefix.length + selectedText.length;
      textarea.focus();
    }, 0);
  };

  const insertLink = () => {
    const url = prompt("URL:");
    if (!url) return;
    wrapSelection("[", "](" + url + ")");
  };

  const handleKeyPress = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const removeAttachment = async (idx) => {
    const att = attachments[idx];
    try {
      await deleteFile(att.publicId);
      setAttachments(prev => prev.filter((_, i) => i !== idx));
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const send = async () => {
    if (!input.trim() && attachments.length === 0) {
      console.log('Send aborted: Empty message and no attachments');
      return;
    }

    setSending(true);
    console.log('Starting send process...');

    const tempId = `temp-${Date.now()}`;
    const tempMessage = {
      tempId,
      formId: mergedTicket.id,
      senderName: currentUserName,
      senderId: startupId,
      senderRole: 'USER',
      message: input,
      attachments,
      timestamp: new Date().toISOString(),
      isOptimistic: true,
      isCurrentUser: true
    };

    console.log('Creating temporary message:', {
      tempMessage,
      attachmentsDetails: attachments.map(a => ({
        name: a.name,
        type: a.type,
        size: a.size
      }))
    });

    setMessages(prev => [...prev, normalizeMessage(tempMessage)]);
    console.log('Optimistic update - message added to local state with tempId:', tempId);

    setInput('');
    setAttachments([]);
    console.log('Cleared input and attachments');

    try {
      if (socketRef.current?.connected) {
        const socketPayload = {
          formId: mergedTicket.id,
          senderId: startupId,
          senderRole: 'startup',
          senderName: currentUserName,
          message: input,
          attachments,
          tempId
        };

        console.log('Emitting sendMessage via socket with payload:', {
          ...socketPayload,
          attachmentsCount: attachments.length
        });

        socketRef.current.emit('sendMessage', socketPayload);
        console.log('Socket emit successful');
      } else {
        const error = new Error('Socket not connected');
        console.error('Socket connection error:', error);
        throw error;
      }
    } catch (err) {
      console.error('Failed to send message - error details:', {
        error: err,
        message: err.message,
        stack: err.stack
      });

      console.log('Rolling back optimistic update for tempId:', tempId);
      setMessages(prev => prev.filter(m => m.tempId !== tempId));
    } finally {
      console.log('Send process completed');
      setSending(false);
    }
  };

  function extractFileName(url) {
    try {
      const { pathname } = new URL(url);
      return pathname.split('/').pop();
    } catch {
      return url;
    }
  }

  const handleFilePreview = (file) => {
    setPreviewFile({
      ...file,
      type: file.url.match(/\.(jpg|jpeg|png|gif|webp|pdf|docs)$/i) ? 'image' : 'doc'
    });
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <CaSidebar isOpen />
      <div className="flex-1 ml-20 flex flex-col">
        <ModernHeader fullName={mergedTicket.assignedCA.name || currentUserName} onClose={onClose} />

        <div className="flex items-center justify-between bg-white px-6 py-4 border-b">
          <div className="flex items-center space-x-4">
            <Link to="/ca-portal"><ArrowLeft /></Link>
            <h1 className="text-md font-semibold">{mergedTicket.title}</h1>
          </div>
        </div>

        <div className="bg-white px-6 border-b">
          <button className="py-3 text-blue-600 border-b-2 border-blue-500">Conversation</button>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col">
          <div ref={scrollRef} className="flex-1 overflow-auto bg-gradient-to-b from-gray-50 to-white p-6 space-y-6">
            {/* Typing indicators */}
            {Object.keys(typingUsers).length > 0 && (
              <div className="flex justify-start mb-2">
                <div className="flex items-start space-x-3 max-w-2xl">
                  <Avatar name={Object.values(typingUsers)[0]} />
                  <div className="bg-white border rounded-2xl px-4 py-2 shadow-sm text-gray-900">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* CONTACT HEADER — put this above fields mapping */}
            {(responseData?._contactEmail || responseData?._contactPhone) && (
              <div className="flex justify-end">
                <div className="flex items-start space-x-3 max-w-2xl flex-row-reverse space-x-reverse">
                  <Avatar name={currentUserName || 'Startup'} />
                  <div className="flex flex-col items-end">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="text-sm font-medium text-gray-900">
                        {currentUserName || 'Startup'}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatTime(responseData?._createdAt || new Date().toISOString())}
                      </span>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-2xl px-4 py-3 shadow-sm text-right">
                      <span className="font-medium text-blue-800">Contact Information:</span>
                      <div className="mt-2 space-y-1 text-sm text-blue-700">
                        {responseData?._contactEmail && (
                          <div className="flex items-center justify-end">
                            <span className="mr-2">📧</span>
                            <span>{responseData._contactEmail}</span>
                          </div>
                        )}
                        {responseData?._contactPhone && (
                          <div className="flex items-center justify-end">
                            <span className="mr-2">📞</span>
                            <span>{responseData._contactPhone}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}


            {fields
              .filter(f => {
                const ans = getAnswer(f);
                // also accept numbers/arrays if needed; here we show only non-empty strings
                return (typeof ans === "string" && ans.trim().length > 0) || (Array.isArray(ans) && ans.length > 0);
              })
              .map(f => {
                const answer = getAnswer(f);
                const answerStr = Array.isArray(answer) ? answer.join(", ") : (answer ?? "");
                const isUrl = typeof answerStr === 'string' && /^https?:\/\//.test(answerStr);
                const display = isUrl ? extractFileName(answerStr) : answerStr;

                return (
                  <div key={f._id} className="flex justify-end">
                    <div className="flex items-start space-x-3 max-w-2xl flex-row-reverse space-x-reverse">
                      <Avatar name={currentUserName || 'U'} />
                      <div className="bg-white border rounded-2xl px-4 py-3 shadow-sm">
                        <p className="text-sm">
                          <span className="font-medium">{f.name}:</span>{' '}
                          {isUrl ? (
                            <div className="mt-2 flex items-center space-x-2">
                              <button
                                onClick={() => handleFilePreview({ name: display, url: answerStr, type: answerStr.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? 'image' : 'doc' })}
                                className="flex items-center space-x-2 p-2 rounded-lg bg-gray-100 hover:bg-gray-200"
                              >
                                {getFileIcon(answerStr.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? 'image' : 'doc')}
                                <span className="truncate max-w-xs">{display}</span>
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); const link = document.createElement('a'); link.href = answerStr; link.download = display; document.body.appendChild(link); link.click(); document.body.removeChild(link); }}
                                className="p-2 rounded-full text-gray-400 hover:text-gray-600"
                                title="Download"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            display
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}

            {messages.map(msg => (
              <div
                key={msg.id}
                className={`flex message-container ${msg.isCurrentUser ? 'justify-end' : 'justify-start'}`}
                data-message-id={msg.id}
              >
                <div className={`flex items-start space-x-3 max-w-2xl ${msg.isCurrentUser ? 'flex-row-reverse space-x-reverse' : ''}`}>
                  <Avatar
                    name={msg.isCurrentUser ? currentUserName : msg.senderName}
                    src={msg.isCurrentUser ? logoUrl : undefined}
                  />
                  <div className={`flex flex-col ${msg.isCurrentUser ? 'items-end' : 'items-start'}`}>
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="text-sm font-medium text-gray-900">
                        {msg.isCurrentUser ? currentUserName : msg.senderName}
                      </span>
                      {msg.timestamp && (
                        <span className="text-xs text-gray-500">{formatTime(msg.timestamp)}</span>
                      )}
                    </div>
                    <div className={`rounded-2xl px-4 py-3 shadow-sm ${msg.isCurrentUser ? 'bg-blue-500 text-white' : 'bg-white border border-gray-200 text-gray-900'}`}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                        {msg.message}
                      </ReactMarkdown>
                      {msg.attachments?.map((att, i) => {
                        const isPdf = att.url?.endsWith('.pdf');
                        const isImage = att.type === 'image' || att.url?.match(/\.(jpg|jpeg|png|gif|webp)$/i);

                        return (
                          <div key={i} className="mt-2 flex items-center space-x-2">
                            <button
                              onClick={() => handleFilePreview(att)}
                              className={`flex items-center space-x-2 p-2 rounded-lg ${msg.isCurrentUser ? 'bg-blue-400 hover:bg-blue-600' : 'bg-gray-100 hover:bg-gray-200'}`}
                            >
                              {isPdf ? (
                                <FileText className="w-4 h-4 text-red-500" />
                              ) : isImage ? (
                                <ImageIcon className="w-4 h-4 text-blue-500" />
                              ) : (
                                <FileText className="w-4 h-4 text-gray-500" />
                              )}
                              <span className="truncate max-w-xs">{att.name}</span>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const link = document.createElement('a');
                                link.href = att.url;
                                link.download = att.name;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }}
                              className={`p-2 rounded-full ${msg.isCurrentUser ? 'text-blue-100 hover:text-white' : 'text-gray-400 hover:text-gray-600'}`}
                              title="Download"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex justify-end mt-1">
                      <MessageStatus status={msg.status} isCurrentUser={msg.isCurrentUser} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {previewFile && (
            <FilePreviewModal
              file={previewFile}
              onClose={() => setPreviewFile(null)}
            />
          )}

          <div className="bg-white border-t p-4 shadow-lg">
            {attachments.length > 0 && (
              <div className="mb-4 bg-gray-50 rounded-lg border p-3">
                <div className="flex justify-between mb-2">
                  <span className="font-medium text-gray-700">
                    Attachments ({attachments.length})
                  </span>
                </div>
                <div className="space-y-2">
                  {attachments.map((att, idx) => (
                    <div key={idx} className="flex items-center space-x-3 bg-white rounded border p-2">
                      {getFileIcon(att.type)}
                      <div className="flex-1 truncate">
                        <p className="truncate font-medium text-gray-900">{att.name}</p>
                      </div>
                      <button
                        onClick={() => removeAttachment(idx)}
                        className="text-gray-400 hover:text-red-500"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mb-3 bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg border p-3 relative">
              <div className="flex space-x-2">
                <button onClick={() => wrapSelection("**")} title="Bold" className="p-2 hover:bg-blue-100 rounded">
                  <strong>B</strong>
                </button>
                <button onClick={() => wrapSelection("*")} title="Italic" className="p-2 hover:bg-blue-100 rounded">
                  <Italic className="w-4" />
                </button>
                <button onClick={insertLink} title="Link" className="p-2 hover:bg-blue-100 rounded">🔗</button>
                <button onClick={() => setShowEmojiPicker(v => !v)} title="Emoji" className="p-2 hover:bg-yellow-100 rounded">
                  😊
                </button>
              </div>
              {showEmojiPicker && (
                <div className="absolute bottom-full left-0 mb-2 z-50">
                  <EmojiPicker onEmojiClick={handleEmojiClick} />
                </div>
              )}
            </div>

            <div className="flex items-end space-x-3">
              <div className="relative flex-1">
                <textarea
                  ref={textareaRef}
                  rows={3}
                  value={input}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyPress}
                  placeholder="Type message..."
                  className="w-full resize-none border rounded-xl px-4 py-3 pr-16 focus:ring-2 focus:ring-blue-500 shadow-sm"
                />
                <div className="absolute bottom-3 right-3 flex space-x-2">
                  <div className="relative">
                    <button
                      onClick={() => setShowAttachMenu(v => !v)}
                      className="p-1.5 hover:bg-blue-100 rounded text-gray-400 hover:text-blue-600"
                    >
                      <Paperclip className="w-4 h-4" />
                    </button>
                    {showAttachMenu && (
                      <div className="absolute bottom-full right-0 mb-2 bg-white border rounded shadow py-2 min-w-[180px] z-50">
                        <button
                          onClick={() => fileInputRef.current.click()}
                          className="w-full px-4 py-2 hover:bg-gray-50 text-left flex items-center space-x-2 text-sm"
                        >
                          <FileText className="w-4 h-4" /><span>Upload Document</span>
                        </button>
                        <button
                          onClick={() => imageInputRef.current.click()}
                          className="w-full px-4 py-2 hover:bg-gray-50 text-left flex items-center space-x-2 text-sm"
                        >
                          <ImageIcon className="w-4 h-4" /><span>Upload Image</span>
                        </button>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={send}
                    disabled={sending || (!input.trim() && attachments.length === 0)}
                    className="p-3 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 disabled:from-gray-300 disabled:to-gray-300 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
                  >
                    <SendIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.txt"
              onChange={handleFileUpload}
              className="hidden"
            />
            <input
              ref={imageInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileUpload}
              className="hidden"
            />
          </div>
        </div>
      </div>
    </div>
  );
}